import { QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import { HttpStatusError } from './http-status-error';

const AUTH_URL = process.env.AUTH_URL as string;

export interface Workspace {
	id: string;
	name: string;
	strategy: AuthenticationStrategy;
	logo?: string;
}

export enum AuthenticationStrategy {
	PASSWORD = 'password',
	SSO = 'sso',
	LDAP = 'ldap',
}

export interface DiscoveryRequest {
	organisationId?: string;
}

export interface OrganisationWithWorkspaces {
	organisationId: string;
	organisationName: string;
	workspaces: Workspace[];
}

export interface DiscoveryResponse {
	email: string;
	workspaces?: Workspace[];
	organisations?: OrganisationWithWorkspaces[];
}

// services/authentication returns grouped `organisations[]` (one entry per org, a single entry for a
// scoped `?organisationId=` call); flat `workspaces[]` is the pre-multi-org shape. Normalizes either
// to a flat list where the org is unambiguous; more than one org returns [] (caller must pick).
export const resolveDiscoveryWorkspaces = (data?: DiscoveryResponse): Workspace[] => {
	if (!data) {
		return [];
	}

	if (data.organisations && data.organisations.length === 1) {
		return data.organisations[0].workspaces;
	}

	return data.workspaces ?? [];
};

// Every discovery entry (one per strategy) for a workspace id, across all orgs. Workspace ids are
// unique, so a lookup by id - unlike listing workspaces - never needs the org scope; deep links and
// "recent workspace" links work for a multi-org email without one.
export const getWorkspaceStrategies = (data: DiscoveryResponse | undefined, workspaceId?: string): Workspace[] => {
	if (!data || !workspaceId) {
		return [];
	}

	const workspaces = data.organisations
		? data.organisations.flatMap((org) => org.workspaces)
		: (data.workspaces ?? []);
	return workspaces.filter(({ id }) => id === workspaceId);
};

export const doDiscovery = async (
	{ organisationId }: DiscoveryRequest,
	init?: RequestInit
): Promise<DiscoveryResponse> => {
	const response = await fetch(
		new URL(!organisationId ? `${AUTH_URL}/identity` : `${AUTH_URL}/identity?organisationId=${organisationId}`),
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
			},
			credentials: 'include',
			...init,
		}
	);

	if (!response.ok) {
		throw new HttpStatusError(response.status, `Unable to get the discovery: ${response.status}`);
	}

	return response.json() as Promise<DiscoveryResponse>;
};

export const useDoDiscovery = <TData>(
	{ organisationId }: DiscoveryRequest,
	options?: UseQueryOptions<DiscoveryResponse, unknown, TData>
): UseQueryResult<TData> => {
	return useQuery<DiscoveryResponse, unknown, TData, QueryKey>(
		['discovery'],
		({ signal }) => doDiscovery({ organisationId }, { signal }),
		{ ...options, staleTime: 10000 }
	);
};
